/**
 * One hook, two ways to turn a nurse talking into medication drafts.
 *
 *   audio_native  We record, upload, and one multimodal model reads the whole
 *                 thing. Hears her self-corrections. The cards appear when she
 *                 stops — a few seconds later for a short list. This is what
 *                 the panel selects on every platform.
 *   device_stt    The phone transcribes; we send text. Nothing but words leaves
 *                 the handset, it costs nothing per minute, and it works with no
 *                 signal. It cannot recover what the recogniser mishears.
 *                 Implemented and reachable, but nothing in the UI picks it.
 *
 * Both converge on POST /medications/extract-from-voice, which writes the same
 * `medication_drafts` rows the photo pipeline writes. Nothing downstream — the
 * hub, the draft cards, promote — knows or cares which one produced them.
 *
 * There used to be a third, `live`: PCM streamed to a server session and fed
 * into a Gemini Live socket, meant to show a card per bottle as she spoke. An
 * audit of every production dictation (Sep 2026) found it never did — its
 * answers arrived only after the turn closed, it reported nothing on 5 of 12
 * clear dictations and dropped drugs on 2 more — while the whole-recording
 * model read all 12 correctly. It was removed; recording is the path.
 */
import {
    useAudioRecorder,
    useAudioRecorderState,
    RecordingPresets,
    requestRecordingPermissionsAsync,
    setAudioModeAsync
} from "expo-audio";
import { useCallback, useEffect, useRef, useState } from "react";
import { Platform } from "react-native";

import { extractFromVoice, getVoiceUploadUrl, type VoiceEngine } from "@/lib/api";
import {
    isOnDevice,
    isSpeechRecognitionAvailable,
    requestSpeechPermission,
    startSpeechRecognition,
    type SpeechRecognitionHandle
} from "@/lib/speech-recognition";
import { uploadFromUri } from "@/lib/upload-from-uri";

export type VoiceCaptureState = "idle" | "starting" | "listening" | "submitting" | "done" | "error";

export interface UseMedicationVoiceCapture {
    state: VoiceCaptureState;
    engine: VoiceEngine;
    setEngine: (engine: VoiceEngine) => void;
    /** Seconds she has been talking, for the timer. */
    seconds: number;
    /** device_stt only — what the recogniser has heard so far. */
    transcript: string;
    error: string | null;
    /** True when this engine can run at all on this build/platform. */
    available: boolean;
    /** Why it cannot run, when `available` is false. */
    unavailableReason: string | null;
    start: () => Promise<void>;
    stop: () => Promise<void>;
    cancel: () => void;
}

const IS_WEB = Platform.OS === "web";

export function useMedicationVoiceCapture(args: {
    visitId: string;
    /** Drug names already on this visit — improves on-device recognition a lot. */
    knownMedicationNames?: string[];
    onSubmitted: (attemptId: number) => void;
}): UseMedicationVoiceCapture {
    const { visitId, knownMedicationNames = [], onSubmitted } = args;

    const [engine, setEngine] = useState<VoiceEngine>("audio_native");
    const [state, setState] = useState<VoiceCaptureState>("idle");
    const [transcript, setTranscript] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [seconds, setSeconds] = useState(0);

    const audioRecorder = useAudioRecorder(RecordingPresets.HIGH_QUALITY);
    const recorderState = useAudioRecorderState(audioRecorder);

    const speechHandle = useRef<SpeechRecognitionHandle | null>(null);
    const finalTranscript = useRef("");
    const tickTimer = useRef<ReturnType<typeof setInterval> | null>(null);

    // ─── Availability ───────────────────────────────────────

    const available = engine === "device_stt" ? isSpeechRecognitionAvailable() : true;
    const unavailableReason =
        engine === "device_stt" && !available
            ? IS_WEB
                ? "This browser has no speech recognition."
                : "On-device dictation needs a dev build (it is not in Expo Go)."
            : null;

    // ─── Timer ──────────────────────────────────────────────

    useEffect(() => {
        if (state === "listening") {
            tickTimer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
        } else if (tickTimer.current) {
            clearInterval(tickTimer.current);
            tickTimer.current = null;
        }
        return () => {
            if (tickTimer.current) {
                clearInterval(tickTimer.current);
                tickTimer.current = null;
            }
        };
    }, [state]);

    // ─── Teardown ───────────────────────────────────────────
    //
    // Every path out of this screen runs through here. A left-open recorder
    // keeps the mic hot with no UI saying so, which is the kind of thing a
    // nurse discovers from her battery.

    const teardown = useCallback(async () => {
        try {
            speechHandle.current?.abort();
        } catch {
            /* already gone */
        }
        speechHandle.current = null;

        try {
            if (recorderState.isRecording) {
                await audioRecorder.stop();
            }
        } catch {
            /* already gone */
        }
    }, [audioRecorder, recorderState.isRecording]);

    // Unmount only. `teardown` is deliberately read through a ref rather than
    // listed as a dependency: it changes identity whenever the recorder ticks,
    // and depending on it would tear the capture down the moment she starts
    // talking.
    const teardownRef = useRef(teardown);
    teardownRef.current = teardown;
    useEffect(() => {
        return () => {
            void teardownRef.current();
        };
    }, []);

    // ─── Engine: device STT ─────────────────────────────────

    const startDeviceStt = useCallback(async () => {
        const granted = await requestSpeechPermission();
        if (!granted) {
            throw new Error("Microphone/speech permission was declined");
        }
        finalTranscript.current = "";
        setTranscript("");
        speechHandle.current = await startSpeechRecognition(
            {
                onResult: (text, isFinal) => {
                    if (isFinal) {
                        finalTranscript.current = text;
                    }
                    // Interims are shown but never sent — the nurse wants to see
                    // that it is hearing her; the extractor wants committed text.
                    setTranscript(text);
                },
                onError: (message) => {
                    setError(message);
                    setState("error");
                },
                onEnd: () => {
                    // The recogniser can stop on its own after a long silence.
                    // Do not treat that as her finishing — she may be in another
                    // room. The screen's Done button is the only thing that ends
                    // a capture.
                }
            },
            { contextualStrings: knownMedicationNames.slice(0, 100) }
        );
    }, [knownMedicationNames]);

    // ─── Engine: record and upload ──────────────────────────

    const startAudioNative = useCallback(async () => {
        const permission = await requestRecordingPermissionsAsync();
        if (!permission.granted) {
            throw new Error("Microphone permission was declined");
        }
        await setAudioModeAsync({ allowsRecording: true, playsInSilentMode: true });
        await audioRecorder.prepareToRecordAsync();
        audioRecorder.record();
    }, [audioRecorder]);

    // ─── Public controls ────────────────────────────────────

    const start = useCallback(async () => {
        setError(null);
        setSeconds(0);
        setState("starting");
        try {
            if (engine === "device_stt") {
                await startDeviceStt();
            } else {
                await startAudioNative();
            }
            setState("listening");
        } catch (e) {
            await teardown();
            setError(e instanceof Error ? e.message : String(e));
            setState("error");
        }
    }, [engine, startAudioNative, startDeviceStt, teardown]);

    const stop = useCallback(async () => {
        if (state !== "listening") {
            return;
        }
        setState("submitting");
        try {
            let result: { attemptId: number };

            if (engine === "device_stt") {
                speechHandle.current?.stop();
                speechHandle.current = null;
                const text = (finalTranscript.current || transcript).trim();
                if (!text) {
                    throw new Error("Nothing was heard — try again, a little closer to the phone.");
                }
                result = await extractFromVoice(visitId, {
                    engine: "device_stt",
                    transcript: text
                });
            } else {
                await audioRecorder.stop();
                const uri = audioRecorder.uri;
                if (!uri) {
                    throw new Error("The recording did not save");
                }
                // Container differs by platform: the web build records webm,
                // native records m4a. The presigner needs to know which, because
                // the content type is baked into the signature.
                const extension = IS_WEB ? "webm" : "m4a";
                const { uploadUrl, s3Key, contentType } = await getVoiceUploadUrl(
                    visitId,
                    extension
                );
                // Streamed from disk with the signed Content-Type. Not a Blob
                // through fetch: since SDK 56 the global fetch is expo/fetch,
                // which replaces the header with the Blob's own (empty) type, so
                // S3 refused the PUT with 403 (signature mismatch).
                await uploadFromUri({ uri, uploadUrl, contentType });
                result = await extractFromVoice(visitId, { engine: "audio_native", s3Key });
            }

            await teardown();
            setState("done");
            onSubmitted(result.attemptId);
        } catch (e) {
            await teardown();
            setError(e instanceof Error ? e.message : String(e));
            setState("error");
        }
    }, [audioRecorder, engine, onSubmitted, state, teardown, transcript, visitId]);

    const cancel = useCallback(() => {
        void teardown();
        setState("idle");
        setSeconds(0);
        setTranscript("");
        finalTranscript.current = "";
        setError(null);
    }, [teardown]);

    return {
        state,
        engine,
        setEngine: (next: VoiceEngine) => {
            if (state === "listening" || state === "submitting") {
                return; // never swap engines mid-capture
            }
            setEngine(next);
            setError(null);
        },
        seconds,
        transcript,
        error,
        available,
        unavailableReason,
        start,
        stop,
        cancel
    };
}

/** Exposed for the screen's copy — "on-device" is only true on a native build. */
export function describeEngine(engine: VoiceEngine): { title: string; blurb: string } {
    switch (engine) {
        case "device_stt":
            return {
                title: "On phone",
                blurb: isOnDevice()
                    ? "The phone does the listening. Nothing but text leaves the device, and it works with no signal."
                    : "The browser does the listening. On a real phone build this runs on-device."
            };
        case "audio_native":
            return {
                title: "Recording",
                blurb: "One model hears the whole thing when you finish. The cards appear a few seconds after you stop, with strengths checked against the medication catalogue."
            };
    }
}
